import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from './cn'

// Static span classes so Tailwind's JIT can see them (no dynamic strings).
const spanMap: Record<number, string> = {
  3: 'md:col-span-3',
  4: 'md:col-span-4',
  5: 'md:col-span-5',
  6: 'md:col-span-6',
  8: 'md:col-span-8',
  9: 'md:col-span-9',
  12: 'md:col-span-12',
}

/** A responsive 12-column grid. Children stack to full width below `md`. */
export function ModuleGrid({ className = '', children, ...p }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('grid min-w-0 max-w-full grid-cols-12 gap-5 items-start', className)} {...p}>
      {children}
    </div>
  )
}

export interface ModuleProps extends HTMLAttributes<HTMLElement> {
  span?: 3 | 4 | 5 | 6 | 8 | 9 | 12
  /** @deprecated Use `onInk`. */
  ink?: boolean
  /** Canonical on-ink spelling; maps to the legacy `ink` prop. */
  onInk?: boolean
}
/** A dashboard module/panel. `span` sizes it inside a ModuleGrid. */
export function Module({ span, ink = false, onInk, className = '', children, ...p }: ModuleProps) {
  const resolvedInk = onInk ?? ink

  return (
    <section
      className={cn(
        'col-span-12 flex flex-col min-w-0 overflow-clip rounded-surface border shadow-card',
        span ? spanMap[span] : '',
        resolvedInk ? 'bg-grad-ink border-veil-line text-on-ink' : 'bg-white border-line',
        className,
      )}
      {...p}
    >
      {children}
    </section>
  )
}

export interface ModuleHeadProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  /** Optional harvest-stable name for the title heading (data-exact-set-name). */
  titleExactName?: string
  /** @deprecated Use `onInk`. */
  ink?: boolean
  /** Canonical on-ink spelling; maps to the legacy `ink` prop. */
  onInk?: boolean
}
export function ModuleHead({
  title,
  subtitle,
  actions,
  titleExactName,
  ink = false,
  onInk,
  className = '',
  children,
  ...p
}: ModuleHeadProps) {
  const resolvedInk = onInk ?? ink

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-5 py-4 border-b',
        resolvedInk ? 'border-veil-line' : 'border-line',
        className,
      )}
      {...p}
    >
      {(title || subtitle) && (
        <div className="min-w-0">
          {title && (
            <h3
              className={cn(
                'font-display font-normal text-h4 leading-snug m-0',
                resolvedInk ? 'text-on-ink' : 'text-strong',
              )}
              data-exact-set-name={titleExactName}
            >
              {title}
            </h3>
          )}
          {subtitle && (
            <p className={cn('m-0 mt-0.5 text-xs', resolvedInk ? 'text-on-ink-tertiary' : 'text-ink-muted')}>
              {subtitle}
            </p>
          )}
        </div>
      )}
      {children}
      {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
    </div>
  )
}

export interface ModuleBodyProps extends HTMLAttributes<HTMLDivElement> {
  flush?: boolean
  scroll?: boolean
}
export function ModuleBody({ flush = false, scroll = false, className = '', children, ...p }: ModuleBodyProps) {
  return (
    <div className={cn('min-w-0 flex-1 min-h-0', !flush && 'p-5', scroll && 'max-h-[340px] overflow-y-auto', className)} {...p}>
      {children}
    </div>
  )
}

export interface ModuleFootProps extends HTMLAttributes<HTMLDivElement> {
  /** @deprecated Use `onInk`. */
  ink?: boolean
  /** Canonical on-ink spelling; maps to the legacy `ink` prop. */
  onInk?: boolean
}
export function ModuleFoot({ ink = false, onInk, className = '', children, ...p }: ModuleFootProps) {
  const resolvedInk = onInk ?? ink

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-5 py-3 border-t text-sm',
        resolvedInk ? 'border-veil-line' : 'border-line',
        className,
      )}
      {...p}
    >
      {children}
    </div>
  )
}
